/**
 * Turns a legal document into something navigable.
 *
 * Shopify renders /policies/* itself, straight into content_for_layout, and
 * the theme gets no say in that markup -- there is no section, no schema and
 * no Liquid hook. So the parts a long legal document needs but Shopify does
 * not emit (a section index, a way to reach the sibling policies) have to be
 * built from the rendered DOM here.
 *
 * Everything this adds is an enhancement: with the script blocked the page is
 * still a correctly styled, fully readable document, just without the index.
 * That is why assets/legal.css keeps the single-column layout as its default
 * and only switches to the two-column grid once this script has added
 * .legal--has-toc.
 *
 * Theme-authored legal pages (templates/page.legal.json) go through
 * sections/legal-document.liquid, which emits .legal__* markup mirroring
 * Shopify's .shopify-policy__* structure precisely so this one script -- and
 * one ruleset in legal.css -- serves both routes.
 */
(() => {
  const container = document.querySelector(".shopify-policy__container, .legal");
  if (!container) return;

  const body = container.querySelector(".shopify-policy__body, .legal__body");
  const prose = container.querySelector(".rte, .legal__prose");
  const titleBlock = container.querySelector(
    ".shopify-policy__title, .legal__header"
  );
  if (!body || !prose) return;

  /* The <h2>s of a policy are its clauses. <h3> is left out of the index on
     purpose: a two-level TOC on a document this long is harder to scan than
     the document it indexes. */
  const headings = [...prose.querySelectorAll("h2")];

  /* ---- Eyebrow and sibling-policy switcher --------------------------------
     The policy list is emitted by layout/theme.liquid from shop.policies, so
     only policies that actually exist on the store appear, and their URLs
     come from Shopify rather than being hardcoded here. */
  if (titleBlock) {
    const heading = titleBlock.querySelector("h1, .legal__title");

    /* The section renders both of these server-side on theme-authored legal
       pages, so only the Shopify-rendered route reaches the branches below. */
    if (heading && !titleBlock.querySelector(".legal__eyebrow")) {
      const eyebrow = document.createElement("span");
      eyebrow.className = "legal__eyebrow";
      eyebrow.textContent = "Legal";
      heading.before(eyebrow);
    }

    const data = document.getElementById("legal-policies-data");
    let policies = [];
    try {
      policies = data ? JSON.parse(data.textContent) : [];
    } catch {
      policies = [];
    }

    /* One link is just this page; a switcher with a single disabled-looking
       pill in it is noise. */
    if (policies.length > 1 && !titleBlock.querySelector(".legal__switcher")) {
      const nav = document.createElement("nav");
      nav.className = "legal__switcher";
      nav.setAttribute("aria-label", "Legal documents");

      const here = window.location.pathname.replace(/\/$/, "");

      policies.forEach((policy) => {
        const link = document.createElement("a");
        link.href = policy.url;
        link.textContent = policy.title;
        if (policy.url.replace(/\/$/, "") === here) {
          link.setAttribute("aria-current", "page");
        }
        nav.append(link);
      });

      titleBlock.append(nav);
    }
  }

  /* A two-or-fewer-section document does not need an index, and giving it one
     costs a whole sidebar column for no navigational gain. */
  if (headings.length < 3) return;

  /* ---- Section ids --------------------------------------------------------
     Policy bodies are authored in the admin rich-text editor, which emits no
     ids, so anchors have to be derived. An id already present wins -- someone
     may have linked to it from elsewhere. */
  const used = new Set();

  const slugify = (text) => {
    const base =
      text
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60) || "section";

    let slug = base;
    let n = 2;
    while (used.has(slug)) slug = `${base}-${n++}`;
    used.add(slug);
    return slug;
  };

  headings.forEach((heading) => {
    if (heading.id) {
      used.add(heading.id);
      return;
    }
    heading.id = slugify(heading.textContent || "");
  });

  /* ---- Build the index ---------------------------------------------------
     <details> rather than a plain <nav>: below 60rem the index collapses to a
     single line so it does not push the document itself off the first screen.
     Above that breakpoint the CSS keeps it open and inert as a sidebar, and
     the media query below keeps the open state matching the layout. */
  const toc = document.createElement("details");
  toc.className = "legal-toc legal-toc--collapsible";

  const summary = document.createElement("summary");
  summary.textContent = "On this page";
  toc.append(summary);

  const list = document.createElement("ol");
  list.className = "legal-toc__list";

  const links = headings.map((heading) => {
    const item = document.createElement("li");
    const link = document.createElement("a");
    link.className = "legal-toc__link";
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent || "";
    item.append(link);
    list.append(item);
    return link;
  });

  toc.append(list);
  body.prepend(toc);
  container.classList.add("legal--has-toc");

  const wide = window.matchMedia("(min-width: 60rem)");
  const syncOpen = () => {
    toc.open = wide.matches;
  };
  syncOpen();
  wide.addEventListener("change", syncOpen);

  /* Tapping a section on mobile should get out of the way rather than leave
     the reader scrolled to a heading with the index still covering it. */
  list.addEventListener("click", () => {
    if (!wide.matches) toc.open = false;
  });

  /* ---- Scroll spy ---------------------------------------------------------
     Marking the section the reader is actually in.

     IntersectionObserver was the first implementation here and it is the wrong
     tool: it only fires when a threshold is *crossed*. Jump straight to an
     anchor, flick-scroll on a phone, or restore a deep scroll position on
     reload, and every heading skipped over goes from "not intersecting, below"
     to "not intersecting, above" without ever changing its intersecting state
     -- so no callback fires and the index stays pinned to whatever it last
     saw. Reading positions directly on scroll has no such blind spot.

     The trigger line sits a third of the way down the viewport, which is
     roughly where a reader's eye rests, rather than at the very top. */
  let queued = false;

  const paint = () => {
    queued = false;

    const line = window.innerHeight / 3;
    let active = 0;

    headings.forEach((heading, i) => {
      if (heading.getBoundingClientRect().top <= line) active = i;
    });

    /* At the very bottom the last section may be too short to ever reach the
       trigger line; without this the index would never mark it. */
    const atEnd =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 2;
    if (atEnd) active = headings.length - 1;

    links.forEach((link, i) => {
      if (i === active) link.setAttribute("aria-current", "true");
      else link.removeAttribute("aria-current");
    });
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(paint);
  };

  addEventListener("scroll", schedule, { passive: true });
  addEventListener("resize", schedule, { passive: true });
  paint();
})();
